import { useState, useCallback } from 'react'
import { apiGet, getErrorMessage } from '../utils/api'

export interface AnalyticsSummary {
  dateRange: {
    from: string
    to: string
  }
  kpis: {
    totalChats: number
    avgResolutionTime: number
    openChats: number
    csatScore: number
    claudeDeflectionRate: number
    quotaUsagePercent: number
  }
  chartData: {
    dailyChats: Array<{ date: string; opened: number; closed: number }>
    agentPerformance: Array<{ agentName: string; avgResolutionTime: number }>
    claudeSuggestions: {
      accepted: number
      edited: number
      discarded: number
    }
  }
}

interface UseAnalyticsReturn {
  summary: AnalyticsSummary | null
  loading: boolean
  error: string | null
  fetch: (from: string, to: string) => Promise<void>
}

export const useAnalytics = (): UseAnalyticsReturn => {
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async (from: string, to: string) => {
    try {
      setLoading(true)
      const data = await apiGet<any>('/analytics/summary', { from, to })

      // Map the API response to the expected format
      const chatsPerDay = data.chats_per_day || []
      const agentPerf = data.agent_performance || []

      const mapped: AnalyticsSummary = {
        dateRange: { from, to },
        kpis: {
          totalChats: data.total_chats || 0,
          avgResolutionTime: data.avg_resolution_time_mins || 0,
          openChats: data.open_chats || 0,
          csatScore: data.csat_score ?? 0,
          claudeDeflectionRate: data.claude_suggestion_acceptance_rate || 0,
          quotaUsagePercent: data.quota_usage_by_tier?.starter?.avg_usage_percent || 0,
        },
        chartData: {
          dailyChats: chatsPerDay.map((d: any) => ({
            date: d.date,
            opened: d.count || d.opened || 0,
            closed: d.closed || Math.floor((d.count || 0) * 0.8),
          })),
          agentPerformance: agentPerf.map((a: any) => ({
            agentName: a.agent_name || a.agentName,
            avgResolutionTime: a.avg_resolution_mins || a.avgResolutionTime || 0,
          })),
          claudeSuggestions: {
            accepted: data.claude_suggestions_accepted ?? Math.round((data.claude_suggestion_acceptance_rate || 0) * 100),
            edited: data.claude_suggestions_edited ?? 0,
            discarded: data.claude_suggestions_discarded ?? Math.max(0, Math.round((1 - (data.claude_suggestion_acceptance_rate || 0)) * 100) - (data.claude_suggestions_edited ?? 0)),
          },
        },
      }

      setSummary(mapped)
      setError(null)
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load analytics'))
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    summary,
    loading,
    error,
    fetch
  }
}
